"use client";

import { useEffect, useRef } from "react";
import Image from "next/image";

import { cn } from "@/lib/utils";

import type { SectionLink, SectionList } from "./types";

type Props = {
  section: SectionLink | SectionList;
  isActive: boolean;
  onNavigate?: (event: React.MouseEvent<HTMLAnchorElement>, id: string) => void;
};

export function ScrollSpyLink({ section, isActive, onNavigate }: Props) {
  const ref = useRef<HTMLAnchorElement>(null);
  const image = "image" in section ? section.image : undefined;
  const tag = "tag" in section ? section.tag : undefined;

  useEffect(() => {
    if (isActive && ref.current) {
      ref.current.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [isActive]);

  return (
    <a
      ref={ref}
      href={`#${section.id}`}
      onClick={(event) => onNavigate?.(event, section.id)}
      className={cn(
        "group flex items-center gap-2 pl-5 pr-6 py-3 w-full transition-colors ease-in-out border-l-4 border-b border-b-border-grayscale-default lg:hover:bg-surface-grayscale-default",
        isActive
          ? "bg-surface-grayscale-lighter lg:bg-transparent border-s-border-secondary-default"
          : "border-l-transparent",
      )}
      data-active={isActive}
    >
      {image && (
        <div className="shrink-0 size-10 overflow-hidden rounded-[4px]">
          <Image
            src={image}
            alt="section preview"
            width={40}
            height={40}
            sizes="40px"
            className="size-10 object-contain mix-blend-multiply"
          />
        </div>
      )}
      <div className={cn("min-w-0 flex-1", !image && "")}>
        <div
          className={cn(
            "inline-flex items-center px-1 py-0.5 rounded-[4px] mb-1 text-[12px] font-medium leading-4",
            tag?.toLowerCase() === "best overall"
              ? "bg-badge-first-bg text-badge-first-text"
              : "bg-badge-default text-badge-default-text",
            !tag && "hidden",
          )}
        >
          {tag}
        </div>
        <div
          className={cn(
            "truncate leading-4",
            image
              ? "text-[12px] text-text-grayscale-caption"
              : "text-sm text-text-grayscale-subtitle font-medium",
          )}
        >
          {section.name}
        </div>
      </div>
    </a>
  );
}
